import { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, Printer, Mail, ArrowRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GiftCardVoucher } from '@/components/GiftCardVoucher';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GiftCardSuccess() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [status, setStatus] = useState('loading'); // loading | paid | pending | failed | error
  const [gift, setGift] = useState(null);
  const attempts = useRef(0);
  const orderId = searchParams.get('order_id') || searchParams.get('codTrans') || '';

  const checkOrder = useCallback(async () => {
    try {
      const params = Object.fromEntries(new URLSearchParams(location.search));
      let res;
      if (params.esito && params.mac) {
        res = await fetch(`${API_URL}/api/giftcard/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...params, codTrans: params.codTrans || orderId }),
        });
      } else {
        res = await fetch(`${API_URL}/api/giftcard/order/${orderId}`);
      }
      if (!res.ok) {
        setStatus('error');
        return;
      }
      const data = await res.json();
      if (data.status === 'paid' && data.gift) {
        setGift(data.gift);
        setStatus('paid');
      } else if (data.status === 'failed') {
        setStatus('failed');
      } else {
        attempts.current += 1;
        if (attempts.current < 6) {
          setStatus('pending');
          setTimeout(() => {
            fetch(`${API_URL}/api/giftcard/order/${orderId}`)
              .then(r => r.json())
              .then(d => {
                if (d.status === 'paid' && d.gift) { setGift(d.gift); setStatus('paid'); }
                else if (d.status === 'failed') setStatus('failed');
                else checkOrder();
              })
              .catch(() => setStatus('pending'));
          }, 3000);
        } else {
          setStatus('pending');
        }
      }
    } catch {
      setStatus('error');
    }
  }, [location.search, orderId]);

  useEffect(() => {
    if (!orderId) {
      setStatus('error');
      return;
    }
    checkOrder();
  }, [orderId, checkOrder]);

  return (
    <>
      <Helmet>
        <title>Gift Card | La Villa - Castelfranco Emilia</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="bg-ivory min-h-screen pt-32 md:pt-40 pb-24 px-6" data-testid="giftcard-success-page">
        <div className="max-w-xl mx-auto">
          {status === 'loading' && (
            <div className="text-center py-20" data-testid="giftcard-status-loading">
              <Loader2 size={40} className="text-champagne mx-auto mb-4 animate-spin" />
              <p className="font-body text-stone-500">Verifica del pagamento in corso...</p>
            </div>
          )}

          {status === 'paid' && gift && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="giftcard-status-paid">
              <div className="text-center mb-8">
                <CheckCircle size={48} className="text-champagne mx-auto mb-4" />
                <h1 className="font-heading text-3xl md:text-4xl text-night mb-3">Il tuo regalo è pronto!</h1>
                <p className="font-body text-stone-600">
                  Pagamento confermato. {gift.email_sent
                    ? <>Ti abbiamo inviato il buono via email a <strong className="text-night">{gift.buyer_email}</strong>.</>
                    : <>Conserva questa pagina: il buono è visibile qui sotto.</>}
                </p>
              </div>

              <div className="mb-6">
                <GiftCardVoucher
                  amount={gift.amount}
                  bonus={gift.bonus_aperitivo}
                  dedication={gift.dedication}
                  recipient={gift.recipient_name}
                />
              </div>

              <div className="bg-white border border-stone-200 rounded-xl p-5 text-center mb-8" data-testid="giftcard-code-box">
                <p className="font-body text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-1">Codice della Gift Card</p>
                <p className="font-heading text-2xl md:text-3xl text-night tracking-[0.15em]" data-testid="giftcard-code">{gift.code}</p>
                <p className="font-body text-xs text-stone-400 mt-2">
                  Presenta questo codice in struttura. Valida fino al 30 settembre 2027.
                </p>
                {gift.bonus_aperitivo > 0 && (
                  <p className="font-body text-xs text-champagne mt-1">
                    Include {gift.bonus_aperitivo} € di bonus dedicati all'aperitivo, separati dal credito.
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => window.print()}
                  variant="outline"
                  className="border-night text-night hover:bg-night hover:text-ivory rounded-full px-8 py-6 text-sm uppercase tracking-widest h-auto"
                  data-testid="giftcard-print-btn"
                >
                  <Printer size={16} className="mr-2" /> Stampa il buono
                </Button>
                <Button
                  asChild
                  className="bg-champagne text-night hover:bg-night hover:text-ivory rounded-full px-8 py-6 text-sm uppercase tracking-widest h-auto"
                >
                  <Link to="/">Torna alla Home <ArrowRight size={16} className="ml-2" /></Link>
                </Button>
              </div>
            </motion.div>
          )}

          {status === 'pending' && (
            <div className="text-center py-20" data-testid="giftcard-status-pending">
              <Clock size={40} className="text-champagne mx-auto mb-4" />
              <h1 className="font-heading text-2xl text-night mb-3">Pagamento in elaborazione</h1>
              <p className="font-body text-stone-500 mb-8 max-w-md mx-auto">
                Stiamo attendendo la conferma dal circuito di pagamento. Di solito richiede pochi istanti:
                appena confermato riceverai il buono via email.
              </p>
              <Button
                onClick={() => { attempts.current = 0; setStatus('loading'); checkOrder(); }}
                className="bg-night text-ivory hover:bg-stone-800 rounded-full px-8"
                data-testid="giftcard-retry-btn"
              >
                Controlla di nuovo
              </Button>
            </div>
          )}

          {status === 'failed' && (
            <div className="text-center py-20" data-testid="giftcard-status-failed">
              <XCircle size={40} className="text-red-400 mx-auto mb-4" />
              <h1 className="font-heading text-2xl text-night mb-3">Pagamento non completato</h1>
              <p className="font-body text-stone-500 mb-8 max-w-md mx-auto">
                Il pagamento è stato annullato o non è andato a buon fine. Nessun importo è stato addebitato:
                puoi riprovare quando vuoi.
              </p>
              <Button
                asChild
                className="bg-champagne text-night hover:bg-night hover:text-ivory rounded-full px-8"
              >
                <Link to="/abbonamenti#gift-card">Torna alle Gift Card</Link>
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-20" data-testid="giftcard-status-error">
              <Mail size={40} className="text-stone-400 mx-auto mb-4" />
              <h1 className="font-heading text-2xl text-night mb-3">Ordine non trovato</h1>
              <p className="font-body text-stone-500 mb-8 max-w-md mx-auto">
                Non riusciamo a verificare questo ordine. Se hai completato il pagamento, controlla la tua email
                oppure scrivici dalla pagina Contatti indicando l'orario dell'acquisto.
              </p>
              <Button asChild variant="outline" className="border-night text-night rounded-full px-8">
                <Link to="/contatti">Contattaci</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
